import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AuditDiffValue } from "@/components/audit/AuditDiffValue";
import { formatValue } from "@/lib/auditDiff";
import { ADMIN_AUDIT_PAGE_SIZE, useAdminAuditHistory } from "@/hooks/useAdminAuditHistory";
import { AdminAuditEntry, AdminAuditSource } from "@/services/adminAuditApi";

interface AdminAuditHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  source: AdminAuditSource;
  /** Undefined while nothing is selected, which also stops the query firing. */
  entityId: string | undefined;
  entityName: string | undefined;
  /** Noun for this kind of entity, e.g. "Prompt". Used in the dialog title. */
  entityLabel: string;
}

const CHANGE_TYPE_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  created: "default",
  updated: "secondary",
  deleted: "destructive",
};

/**
 * Read-only change history for one admin-managed entity.
 *
 * The same dialog serves prompts and API configurations: the two live in different
 * services but their audit tables are identical, so only the fetch path differs.
 *
 * Diffs are rendered as they arrive rather than turned into sentences. The server
 * computes them field by field, so the shape is already readable, and templating it
 * would need maintaining every time a tracked field is added.
 */
export const AdminAuditHistoryDialog = ({
  open,
  onClose,
  source,
  entityId,
  entityName,
  entityLabel,
}: AdminAuditHistoryDialogProps) => {
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // The dialog stays mounted between opens, so reset the pager and the expanded row
  // each time it opens or switches entity rather than only on first mount.
  useEffect(() => {
    setPage(1);
    setExpandedId(null);
  }, [entityId, open]);

  const { data, isLoading, isError } = useAdminAuditHistory(source, open ? entityId : undefined, page);

  const entries = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / ADMIN_AUDIT_PAGE_SIZE));

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-[1100px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{entityLabel} History</DialogTitle>
          <DialogDescription>
            {entityName ? `Changes to "${entityName}", most recent first.` : "Most recent changes first."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto border rounded-md">
          <AdminAuditTable
            entries={entries}
            isLoading={isLoading}
            isError={isError}
            expandedId={expandedId}
            onToggle={(id) => setExpandedId((prev) => (prev === id ? null : id))}
          />
        </div>

        {total > 0 && (
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              {total} {total === 1 ? "entry" : "entries"} · page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const AdminAuditTable = ({
  entries,
  isLoading,
  isError,
  expandedId,
  onToggle,
}: {
  entries: AdminAuditEntry[];
  isLoading: boolean;
  isError: boolean;
  expandedId: string | null;
  onToggle: (id: string) => void;
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading history...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-10 text-center text-sm text-destructive">
        Could not load the change history.
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-muted-foreground">
        No changes have been recorded yet.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-8" />
          <TableHead>Date / Time</TableHead>
          <TableHead>Changed By</TableHead>
          <TableHead>Change Type</TableHead>
          <TableHead>Changed Fields</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.map((entry) => (
          <AdminAuditRow
            key={entry.id}
            entry={entry}
            isExpanded={expandedId === entry.id}
            onToggle={() => onToggle(entry.id)}
          />
        ))}
      </TableBody>
    </Table>
  );
};

/** Email is what a reader recognises; the sub is an opaque id, shown only as a fallback. */
const actorLabel = (entry: AdminAuditEntry): string =>
  entry.changed_by_email ?? entry.changed_by_sub ?? "—";

const AdminAuditRow = ({
  entry,
  isExpanded,
  onToggle,
}: {
  entry: AdminAuditEntry;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  const changedFields = useMemo(() => Object.keys(entry.diff ?? {}), [entry.diff]);
  const Chevron = isExpanded ? ChevronDown : ChevronRight;

  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell>
          <Chevron className="w-4 h-4" />
        </TableCell>
        <TableCell className="whitespace-nowrap">{new Date(entry.changed_at).toLocaleString()}</TableCell>
        <TableCell className="break-all">{actorLabel(entry)}</TableCell>
        <TableCell>
          <Badge variant={CHANGE_TYPE_VARIANT[entry.change_type] ?? "outline"}>{entry.change_type}</Badge>
        </TableCell>
        <TableCell className="text-sm">
          {changedFields.length > 0 ? changedFields.join(", ") : "No fields changed"}
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow>
          <TableCell colSpan={5} className="bg-muted/30">
            {changedFields.length === 0 ? (
              <div className="py-2 text-sm text-muted-foreground">
                This save did not change any tracked field.
              </div>
            ) : (
              <dl className="py-2 space-y-3">
                {changedFields.map((field) => (
                  <AdminAuditFieldRow key={field} field={field} change={entry.diff[field]} />
                ))}
              </dl>
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

/** Above this, a string value is prose rather than a field value and gets the line diff. */
const LONG_TEXT_LENGTH = 200;

const isLongText = (value: unknown): value is string =>
  typeof value === "string" && (value.length > LONG_TEXT_LENGTH || value.includes("\n"));

const asText = (value: unknown): string => (typeof value === "string" ? value : "");

const isStructured = (value: unknown): boolean => typeof value === "object" && value !== null;

/** JSON values are shown indented rather than as the one-liner formatValue produces. */
const asJson = (value: unknown): string => (value === null || value === undefined ? "—" : JSON.stringify(value, null, 2));

const AdminAuditFieldRow = ({
  field,
  change,
}: {
  field: string;
  change: { old: unknown; new: unknown };
}) => {
  const label = <dt className="text-xs font-semibold text-muted-foreground mb-1">{field}</dt>;

  // Prose — a priming or description rewrite — gets the line-level diff, so the reader
  // is not left comparing two walls of text.
  if (isLongText(change.old) || isLongText(change.new)) {
    return (
      <div>
        {label}
        <dd className="grid grid-cols-2 gap-4">
          <AuditDiffValue before={asText(change.old)} after={asText(change.new)} side="before" />
          <AuditDiffValue before={asText(change.old)} after={asText(change.new)} side="after" />
        </dd>
      </div>
    );
  }

  // Objects and arrays — an api_configuration blob, a list of format-type ids — are
  // shown indented side by side. The JSON is taken to be self-explanatory.
  if (isStructured(change.old) || isStructured(change.new)) {
    return (
      <div>
        {label}
        <dd className="grid grid-cols-2 gap-4">
          <pre className="text-xs font-mono whitespace-pre-wrap break-words max-h-64 overflow-auto bg-red-100/40 rounded p-2">
            {asJson(change.old)}
          </pre>
          <pre className="text-xs font-mono whitespace-pre-wrap break-words max-h-64 overflow-auto bg-green-100/40 rounded p-2">
            {asJson(change.new)}
          </pre>
        </dd>
      </div>
    );
  }

  return (
    <div>
      {label}
      <dd className="flex items-start gap-2 text-sm font-mono break-all">
        <span className="bg-red-100/60 rounded px-1">{formatValue(change.old)}</span>
        <span aria-hidden className="text-muted-foreground">→</span>
        <span className="bg-green-100/60 rounded px-1">{formatValue(change.new)}</span>
      </dd>
    </div>
  );
};
